import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import ChatshopLogo from 'assets/media/images/Chatshop-long-logo.svg';
import PagiiLogo from 'assets/media/images/Pagii-icon.png';

import LoginLayout from "./login.layout";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import useRedux from "redux/useRedux";
import { AuthResetPassword } from "redux/auth/auth.services";
import "react-toastify/dist/ReactToastify.css";
import { Error } from "@mui/icons-material";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { thunkDispatch } = useRedux();

  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [payloads, setPayloads] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setIsSuccess(
      payloads.confirmPassword === payloads.password &&
        payloads.password !== "" &&
        payloads.password.length >= 8
    );
  }, [payloads]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!isSuccess) return;
    if (!token) {
      setErrorMsg("Token reset password tidak valid atau tidak ditemukan.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    thunkDispatch(AuthResetPassword({ token, newPassword: payloads.password }))
      .unwrap()
      .then((res: any) => {
        if (res && res.status === "error") {
          setErrorMsg(res.data || "Gagal mereset kata sandi");
          setIsSubmitting(false);
        } else {
          toast.success("Kata sandi berhasil diubah! Silakan login kembali.");
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      })
      .catch(() => {
        setErrorMsg("Terjadi kesalahan sistem. Silakan coba lagi.");
        setIsSubmitting(false);
      });
  };

  return (
    <LoginLayout title="Pagii Chatshop - Ubah Kata Sandi" fullScreen>
      <ToastContainer />
      <div className="flex h-screen w-full flex-col lg:flex-row overflow-hidden">
        <div className="relative hidden w-full lg:block lg:w-3/5 h-full overflow-hidden shrink-0">
          <img
            src="/login-cover-2.jpeg"
            alt="Login Illustration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent flex flex-col justify-end p-10 lg:p-16">
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl p-6 lg:p-8 shadow-2xl max-w-md">
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/ic-pagii-chatshop-small.svg"
                  alt="Chatshop Logo"
                  className="h-8 w-auto"
                />
                <span className="text-2xl font-bold text-white tracking-widest ">
                  CHATSHOP
                </span>
              </div>
              <h2 className="text-lg text-white font-medium leading-relaxed ">
                Solusi Terpadu Kelola Bisnis & Percakapan Pelanggan dalam Satu Platform yang Cerdas.
              </h2>
            </div>
          </div>
        </div>

        <div className="flex flex-1 w-full items-center justify-center bg-white p-8 lg:w-2/5 lg:p-12 overflow-y-auto">
          <div className="w-full max-w-md space-y-8">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <div className="flex items-center gap-2.5 mb-8">
                <a href="https://chatshop.pagii.co/" target="_blank" rel="noopener noreferrer">
                  <img
                    src={ChatshopLogo}
                    alt="Chatshop"
                    className="h-12 w-auto"
                  />
                </a>
              </div>

              <h1 className="text-4xl font-bold tracking-tight text-slate-900">
                Ubah Kata Sandi
              </h1>
              <p className="mt-2 text-sm text-slate-400">
                Silahkan masukkan kata sandi baru Anda.
              </p>
            </div>

            {errorMsg && (
              <div
                className="bg-red-50 border-2 border-red-500 text-red-700 px-4 py-3 rounded-lg relative text-sm flex items-center gap-2"
                role="alert"
              >
                <Error className="text-xl" />
                <span className="block sm:inline">{errorMsg}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="mt-10 space-y-6">
              <div className="group relative">
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    minLength={8}
                    value={payloads.password}
                    onChange={(e) => {
                      setPayloads({
                        ...payloads,
                        password: e.target.value,
                      });
                      setErrorMsg(null);
                    }}
                    className={`block w-full rounded-lg border-2 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-0 transition-all ${
                      payloads.password !== "" && payloads.password.length < 8
                        ? "border-red-500"
                        : "border-slate-200"
                    }`}
                    placeholder="Kata sandi baru"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-4 flex items-center text-slate-400 hover:text-brand transition-colors focus:outline-none"
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                      className="h-5 w-5"
                    />
                  </button>
                </div>
                <div className="h-4">
                  {payloads.password !== "" && payloads.password.length < 8 && (
                    <p className="mt-1 text-xs text-red-500 ">
                      Kata sandi minimum 8 karakter
                    </p>
                  )}
                </div>
              </div>

              <div className="group relative">
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    minLength={8}
                    value={payloads.confirmPassword}
                    onChange={(e) => {
                      setPayloads({
                        ...payloads,
                        confirmPassword: e.target.value,
                      });
                      setErrorMsg(null);
                    }}
                    className={`block w-full rounded-lg border-2 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-0 transition-all ${
                      payloads.confirmPassword !== "" && payloads.password !== payloads.confirmPassword
                        ? "border-red-500"
                        : "border-slate-200"
                    }`}
                    placeholder="Konfirmasi kata sandi baru"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-4 flex items-center text-slate-400 hover:text-brand transition-colors focus:outline-none"
                  >
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                      className="h-5 w-5"
                    />
                  </button>
                </div>
                <div className="h-4">
                  {payloads.confirmPassword !== "" && payloads.password !== payloads.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500 ">
                      Kata sandi konfirmasi tidak sesuai
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-brand hover:text-brand-dark transition-colors"
                >
                  Kembali ke Login
                </button>
                <button
                  type="submit"
                  disabled={!isSuccess || isSubmitting}
                  className={`px-8 rounded-lg py-3 text-base font-bold shadow-lg transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-[0.98] ${
                    isSuccess && !isSubmitting
                      ? "bg-brand text-white hover:bg-brand-dark focus-visible:outline-brand-dark"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {isSubmitting ? "Memproses..." : "Ubah kata sandi"}
                </button>
              </div>

              <div className="flex justify-end mt-4">
                <div className="fixed bottom-4">
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <span>Powered by</span>
                    <a href="https://pagii.co/" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-75">
                      <img src={PagiiLogo} alt="Pagii" className="h-4 w-auto inline" />
                    </a>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LoginLayout>
  );
};

export default ResetPasswordPage;
